import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useMemo } from 'react';
import { Box, Typography, Select, MenuItem, InputBase } from '@mui/material';
import { EventStatus as EventStatusEnum } from 'enum/event-status.enum';
import { RoleMap, useGetRole } from 'hooks/useGetRole';
import { useProfile } from 'hooks';
import { Event } from 'services/api';
import { getPrimaryHostOrganization } from 'components/pages/event-detail/approval-utils';

export interface EventAdminStatusProps {
  event: Event;
  status: EventStatusEnum;
  onChange: (status: EventStatusEnum) => void;
  marginBottom?: string;
}

export const newLabels = {
  LIVE_ON_APP: 'Live on App',
  REQUIRES_EDITS: 'Requires Edits',
  NEEDS_REVIEW: 'Needs Review',
  CANCELED: 'Canceled',
  EXPIRED: 'Expired',
};

export const multiStepApprovalLabels = {
  CANCELED: 'Canceled',
};

const statusStylesBackgroundNew: Record<
  EventStatusEnum,
  { backgroundColor: string; border: string; borderRadius: string }
> = {
  LIVE_ON_APP: {
    backgroundColor: '#E7F9F1',
    border: '1px solid #E7F9F1',
    borderRadius: '8px',
  },
  REQUIRES_EDITS: {
    backgroundColor: '#EEE2E2',
    border: '1px solid #EEE2E2',
    borderRadius: '8px',
  },
  NEEDS_REVIEW: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #DEDEDE',
    borderRadius: '8px',
  },
  CANCELED: {
    backgroundColor: '#DEDEDE',
    border: '1px solid #DEDEDE',
    borderRadius: '8px',
  },
  EXPIRED: {
    backgroundColor: '#DEDEDE',
    border: '1px solid #DEDEDE',
    borderRadius: '8px',
  },
};

const statusStylesTextNew: Record<EventStatusEnum, { color: string }> = {
  LIVE_ON_APP: { color: '#00980B' },
  REQUIRES_EDITS: { color: '#9B0000' },
  NEEDS_REVIEW: { color: '#383838' },
  CANCELED: { color: '#383838' },
  EXPIRED: { color: '#383838' },
};

const StatusBox = ({ status }: { status: EventStatusEnum }) => {
  return (
    <Box
      sx={{
        ...statusStylesBackgroundNew[status],
        justifyContent: 'flex-start',
        alignItems: 'center',
        display: 'flex',
        height: '50px',
        cursor: 'pointer',
        paddingX: '16px',
        gap: '4px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '10px',
          height: '10px',
          backgroundColor: statusStylesTextNew[status].color,
          borderRadius: '50%',
        }}
      ></Box>
      <Typography
        sx={{
          ...statusStylesTextNew[status],
          fontSize: pxToEm(14),
          fontWeight: 400,
        }}
      >
        {newLabels[status]}
      </Typography>
    </Box>
  );
};

export const EventAdminStatus = ({
  event,
  status,
  onChange,
  marginBottom = '16px',
}: EventAdminStatusProps): ReactElement => {
  const role = useGetRole();
  const { profile } = useProfile();

  const selectLabels = useMemo(() => {
    if (profile?.school?.isMultiStepApprovalEnabled && role?.includes(RoleMap.admin))
      return multiStepApprovalLabels;
    return newLabels;
  }, [role, profile]);

  const isEditable = () => {
    if (event.status === 'EXPIRED') return false;
    if (role?.includes(RoleMap.admin)) {
      const primary = getPrimaryHostOrganization(event.hostOrganizations);
      return Boolean(
        primary &&
          profile?.adminOrganizations.some(org => org.id === primary.id),
      );
    }

    return [
      RoleMap['executive-admin'],
      RoleMap.master,
    ].some(r => role?.includes(r));
  };

  return (
    <Box sx={{ marginBottom }}>
      <Select
        value={status}
        readOnly={!isEditable()}
        input={<InputBase />}
        onChange={event => onChange(event.target.value as EventStatusEnum)}
        renderValue={selected => (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <StatusBox status={selected as EventStatusEnum} />
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              width: '280px',
              marginTop: '4px',
              border: '1px solid #DEDEDE',
              boxShadow: 'none',
            },
          },
        }}
        sx={{
          width: '280px',
          height: '50px',
          '& .MuiSelect-select': {
            padding: '0 !important',
          },
          '& .MuiSelect-icon': {
            display: isEditable() ? 'block' : 'none',
            color: statusStylesTextNew[status].color,
          },
        }}
      >
        {Object.entries(selectLabels)
          .filter(([value]) => value !== 'EXPIRED')
          .map(([value]) => (
          <MenuItem
            key={value}
            value={value}
            sx={{
              padding: '5px 5px',
            }}
          >
            <StatusBox status={value as EventStatusEnum} />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};
